import { useState } from 'react';

/**
 * useDispatchPagination - Hook for managing pagination state and selectable record counts
 *
 * This hook handles:
 * - Current page number
 * - Pagination metadata from API
 * - Selectable record counts for different modes (bill, print, create bill)
 *
 * @param {Object} options - Configuration options
 * @param {number} options.initialPage - Initial page number from URL (default: 1)
 *
 * @returns {Object} Pagination state and setters
 */
export const useDispatchPagination = ({ initialPage = 1 } = {}) => {
  // =====================================================================
  // PAGINATION STATE
  // =====================================================================

  /** Current page number (1-indexed) */
  const [pageNumber, setPageNumber] = useState(initialPage);

  /** Pagination metadata from API (total_records, total_pages, current_page, etc.) */
  const [paginationData, setPaginationData] = useState({});

  // =====================================================================
  // SELECTABLE RECORDS STATE
  // Counts used for "Select All" functionality in different modes
  // =====================================================================

  /**
   * Total records selectable for group dispatch (bill mode)
   * Only draft status records with appropriate vendor type are selectable
   */
  const [selectableRecords, setSelectableRecords] = useState(null);

  /**
   * Total records selectable for creating bills
   * Only dispatched/received records without existing bills are selectable
   */
  const [createBillSelectableRecords, setCreateBillSelectableRecords] =
    useState(null);

  /**
   * Total records selectable for printing packing lists
   * May have vendor-specific filtering (e.g., only dyeing partners for ESUN)
   */
  const [printPackingSelectableRecords, setPrintPackingSelectableRecords] =
    useState(null);

  // =====================================================================
  // RETURN VALUE
  // =====================================================================

  return {
    // Pagination state
    pageNumber,
    setPageNumber,
    paginationData,
    setPaginationData,

    // Selectable record counts (populated by useDispatchData)
    selectableRecords,
    setSelectableRecords,
    createBillSelectableRecords,
    setCreateBillSelectableRecords,
    printPackingSelectableRecords,
    setPrintPackingSelectableRecords,
  };
};
